import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { ClientConfigurationLanguage, GenericAdminFilterLanguage, Router as RouterLang } from '../../../../../../i18n'
import { PageTitle } from '../../../Molecules/PageTitle/PageTitle'
import { DataTable } from '../../../Molecules/DataTable/DataTable'
import { AdminFilters, AdminFilterSingleValues, AdminFilterValues } from '../../../Molecules/AdminFilters/AdminFilters'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { AdminBackendPermissionNames } from '../../../../../React/Common/Constants/Permissions'
import { PaginationInfo } from '../../../../../NonReact/Types/Types'
import {
    ClientConfigurationEntry,
    ClientConfigurations,
    ClientConfigurationsResponse
} from '../../../../../NonReact/Types/ClientConfigurationTypes'
import { ClientConfigurationModal } from '../Modal/ClientConfigurationModal'
import { SelectedEntry } from '../Modal/Types/Types'
import { CMEFields } from '../../../../../NonReact/constants/ApiConstants'
import { CME } from '../../../../../React/Common/Types/Types'
import { isBitmaskMatching } from '../../../../../React/Common/Utils/BitwiseOperations'
import { CapabilityBitFlags } from '../../../../../React/CMEAddModify/Constants'
import { getClientConfigurationsList, getCMEsAsSelectOptions } from '../../../../../React/Common/API/ItemsGetters'
import { SelectOptions } from '../../../../../React/Common/Components/Selects'
import { ArrayUtils } from '../../../../../NonReact/utils/ArrayUtils'
import { ALL_VALUE } from '../../../../../React/Common/Constants/DefaultValues'
import { deleteClientConfigurations } from '../../../../../NonReact/common/API/EntityAPIClient'
import { DEFAULT_CME_ID } from '../../../../../React/Common/Constants/ServerValues'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import { IsVarEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'

const DEFAULT_ROWS_PER_PAGE: number = 25

const cmeFiltering: [string[], (cme: CME) => boolean] = [
    [CMEFields.CAPABILITIES],
    (cme: CME): boolean =>
        isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_BROADWORKS) ||
        isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_WEBEX) ||
        isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_TEAMS)
]

export const ClientConfigurationList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ClientConfigurationLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [clientConfigurationList, setClientConfigurationList]: ReactState<Nullable<ClientConfigurations>> =
        useState<Nullable<ClientConfigurations>>()
    const [selectedFilters, setSelectedFilters]: ReactState<Nullable<AdminFilterSingleValues>> =
        useState<Nullable<AdminFilterSingleValues>>(null)
    const [loading, setLoading]: ReactState<number> = useState<number>(0)
    const [sort, setSort]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [filters, setFilters]: ReactState<Nullable<AdminFilterValues>> = useState<Nullable<AdminFilterValues>>()
    const [pagination, setPagination]: ReactState<PaginationInfo> = useState<PaginationInfo>([0, DEFAULT_ROWS_PER_PAGE])
    const [totalCount, setTotalCount]: ReactState<number> = useState<number>(0)
    const [selectedEntry, setSelectedEntry]: ReactState<Definable<SelectedEntry>> = useState<Definable<SelectedEntry>>()
    const openState: ReactState<boolean> = useState<boolean>(false)
    const [isOpen, setIsOpen]: ReactState<boolean> = openState
    const [isViewOnly, setIsViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [isAllowedView, setIsAllowedView]: ReactState<boolean> = useState<boolean>(true)

    const [userInfo]: UserInfo = useUserInfo()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const getClientConfigurations = useCallback((): void => {
        if (IsVarEmpty(userInfo?.Scope?.TelSysID) || IsVarEmpty(userInfo?.Scope?.PartIDInTelSys)) return

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination

        getClientConfigurationsList(
            filters?.telephonyServer?.value ?? userInfo?.Scope?.TelSysID[0] ?? ALL_VALUE,
            filters?.partition?.value ?? userInfo?.Scope?.PartIDInTelSys[0] ?? ALL_VALUE,
            currentPage * rowsPerPage,
            rowsPerPage,
            sort,
            filters?.search
        )
            .then((clientConfigurationListLocal: Nullable<ClientConfigurationsResponse>): void => {
                if (isMounted.current) {
                    setClientConfigurationList(
                        clientConfigurationListLocal?.Data?.Items?.map(
                            (clientConfig: ClientConfigurationEntry): ClientConfigurationEntry => ({
                                ...clientConfig,
                                key: clientConfig.ID
                            })
                        )
                    )
                    setTotalCount(clientConfigurationListLocal?.Data?.Total ?? 0)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        filters,
        sort,
        pagination,
        JSON.stringify(userInfo?.Scope?.TelSysID),
        JSON.stringify(userInfo?.Scope?.PartIDInTelSys)
    ])

    useEffect(getClientConfigurations, [
        filters,
        sort,
        pagination,
        JSON.stringify(userInfo?.Scope?.TelSysID),
        JSON.stringify(userInfo?.Scope?.PartIDInTelSys)
    ])

    useEffect((): void => {
        getCMEsAsSelectOptions(...cmeFiltering)
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) setIsAllowedView(!ArrayUtils.isEmpty(cmes))
            })
            .catch((e: Error): void => {
                if (isMounted.current) displayError(e)
            })
    }, [])

    return (
        <div className='cloud-configuration-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_clientConfiguration}></PageTitle>
            {isAllowedView ? (
                <div className='ccl-table-container admin-table-list-container'>
                    <AdminFilters
                        allowCXIOnly
                        localeText={{
                            cmeLabel: language.get(ClientConfigurationLanguage.ClientConfiguration_Admin_Filters_CME),
                            telephonyServerLabel: language.get(
                                ClientConfigurationLanguage.ClientConfiguration_Table_Headers_TelSys
                            ),
                            partitionLabel: language.get(
                                ClientConfigurationLanguage.ClientConfiguration_Table_Headers_Partition
                            ),
                            includeChildCmeLabel: language.get(
                                ClientConfigurationLanguage.ClientConfiguration_Admin_Filters_IncludeChildCMEs
                            ),
                            searchLabel: language.get(
                                ClientConfigurationLanguage.ClientConfiguration_Admin_Filters_Search
                            )
                        }}
                        onChange={(values: AdminFilterValues): void => setFilters(values)}
                        optionsToDisplay={{
                            cme: true,
                            telephonyServer: true,
                            partition: true,
                            crmType: false,
                            scope: false,
                            codeType: false,
                            userType: false,
                            includeCmeChildren: true,
                            search: true,
                            includeRefresh: true
                        }}
                        onRefresh={getClientConfigurations}
                        cmeFiltering={cmeFiltering}
                    />
                    <DataTable
                        permissionName={AdminBackendPermissionNames.INTEGRATOR_CLIENT}
                        localeText={{
                            addButton: language.get(ClientConfigurationLanguage.ClientConfiguration_Footer_Add),
                            viewButton: language.get(ClientConfigurationLanguage.ClientConfiguration_Footer_View),
                            changeButton: language.get(ClientConfigurationLanguage.ClientConfiguration_Footer_Change),
                            deleteButton: language.get(ClientConfigurationLanguage.ClientConfiguration_Footer_Delete),
                            selectionActive: (count: number): string =>
                                language.getInterpolated(
                                    ClientConfigurationLanguage.ClientConfiguration_Footer_Selected,
                                    count
                                ),
                            noRows: language.get(ClientConfigurationLanguage.ClientConfiguration_Footer_NoRows)
                        }}
                        defaultRowsPerPageOptions={[10, 25, 50, 100]}
                        defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                        totalCount={totalCount}
                        colIndexToTriggerClick={0}
                        isSelectable
                        isLoading={loading > 0}
                        paginationServer
                        sortServer
                        onToolbarViewClick={([selectedItem]: ClientConfigurations): void => {
                            setSelectedEntry({
                                telsysID: selectedItem.TelSysID,
                                partitionID: selectedItem.PartitionID,
                                deviceID: selectedItem.DeviceNumber,
                                id: selectedItem.ID
                            })
                            setIsViewOnly(true)
                            setIsOpen(true)
                        }}
                        onToolbarChangeClick={([selectedItem]: ClientConfigurations): void => {
                            setSelectedEntry({
                                telsysID: selectedItem.TelSysID,
                                partitionID: selectedItem.PartitionID,
                                deviceID: selectedItem.DeviceNumber,
                                id: selectedItem.ID
                            })
                            setIsViewOnly(false)
                            setIsOpen(true)
                        }}
                        onToolbarAddClick={(): void => {
                            setSelectedEntry(undefined)
                            setSelectedFilters({
                                cme: filters?.cme?.value ?? DEFAULT_CME_ID,
                                telephonyServer: filters?.telephonyServer?.value ?? ALL_VALUE,
                                partition: filters?.partition?.value ?? ALL_VALUE,
                                device: EMPTY_STRING
                            })
                            setIsViewOnly(false)
                            setIsOpen(true)
                        }}
                        onToolbarDeleteClick={(selectedItems: ClientConfigurations): void => {
                            setLoading((currentLoading: number): number => currentLoading + 1)
                            deleteClientConfigurations(selectedItems)
                                .then(getClientConfigurations)
                                .catch((e: Error): void => displayError(e))
                                .finally((): void => setLoading((currentLoading: number): number => currentLoading - 1))
                        }}
                        onSortChange={setSort}
                        onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                            setPagination([currentPage, newRowsPerPage])
                        }
                        onPageChange={(newPage: number, rowsPerPage: number): void =>
                            setPagination([newPage, rowsPerPage])
                        }
                        columns={[
                            {
                                headerName: language.get(
                                    ClientConfigurationLanguage.ClientConfiguration_Table_Headers_Description
                                ),
                                field: 'Description',
                                key: 'ccltv-description',
                                sortable: true
                            },
                            {
                                headerName: language.get(
                                    ClientConfigurationLanguage.ClientConfiguration_Table_Headers_TelSys
                                ),
                                field: 'TelSysDescription',
                                key: 'ccltv-telsys',
                                sortable: true
                            },
                            {
                                headerName: language.get(
                                    ClientConfigurationLanguage.ClientConfiguration_Table_Headers_Partition
                                ),
                                field: 'PartitionDescription',
                                key: 'ccltv-partition',
                                sortable: true
                            },
                            {
                                headerName: language.get(
                                    ClientConfigurationLanguage.ClientConfiguration_Table_Headers_Device
                                ),
                                field: 'DeviceDescription',
                                key: 'ccltv-device',
                                sortable: true
                            }
                        ]}
                        data={clientConfigurationList ?? []}
                    />
                    {isOpen ? (
                        <ClientConfigurationModal
                            openState={openState}
                            filters={selectedFilters}
                            selectedEntry={selectedEntry}
                            isViewOnly={isViewOnly}
                            closeModal={(refresh?: boolean): void => {
                                setIsOpen(false)

                                if (refresh) {
                                    getClientConfigurations()
                                }
                            }}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                <div>
                    <h3 className='ccl-no-cmes'>
                        {language.get(ClientConfigurationLanguage.ClientConfiguration_No_Available_CMEs)}
                    </h3>
                </div>
            )}
        </div>
    )
}
